import { forwardRef, useEffect, useImperativeHandle, useState, type MouseEvent, type ReactNode } from 'react';
import { Presenter, type SlideModalCtaOption } from './Presenter';

type Props = {
  children?: ReactNode;
  overlayClassName?: string;
  containerClassName?: string;
  ctaOption?: SlideModalCtaOption;
  withDefaultLayout?: boolean;
  shouldCloseOnClick?: boolean;
  onCloseModal?: () => void;
};

export type SlideModalHandler = {
  open?: () => void;
  close?: () => void;
};

export const SlideModal = forwardRef<SlideModalHandler, Props>(
  (
    { overlayClassName, containerClassName, children, ctaOption, withDefaultLayout, shouldCloseOnClick, onCloseModal },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    const closeModal = () => {
      setIsOpen(false);
      onCloseModal?.();
    };

    const handleContentClick = (event: MouseEvent) => {
      event.stopPropagation();
    };

    useEffect(() => {
      if (isOpen) {
        document.body.classList.add('no-scroll');
      }
      return () => document.body.classList.remove('no-scroll');
    }, [isOpen]);

    useImperativeHandle(ref, () => ({
      open() {
        setIsOpen(true);
      },
      close() {
        closeModal();
      }
    }));

    return (
      <Presenter
        isOpen={isOpen}
        overlayClassName={overlayClassName}
        containerClassName={containerClassName}
        shouldCloseOnClick={shouldCloseOnClick}
        closeModal={closeModal}
        handleContentClick={handleContentClick}
        ctaOption={ctaOption}
        withDefaultLayout={withDefaultLayout}
      >
        {children}
      </Presenter>
    );
  }
);

SlideModal.displayName = 'SlideModal';
